import { ACTION_VIEW_PAGE } from 'utils/analytics/eventTypeConstantsAmplitude';
import { ExperimentVariant } from 'actions/ExperimentActions';
import { PageType } from 'utils/analytics/eventTypesConstants';
import { StoreType } from '@tock/shared/utils/connect';
import {
  amplitudeSetUserId,
  amplitudeSetUserProperty,
  amplitudeSetUserPropertyOnce,
  businessToData,
  revokeAmplitudeTracking,
  setupAmplitudeTracking,
} from 'utils/analytics/amplitude';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getTockAmplitudePropertiesSelector } from 'selectors/appSelector';
import { isPaymentCardHoldExperience } from 'utils/priceUtils';
import { parse } from '@tock/query-string';
import { uniq } from 'es-toolkit/compat';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsentRestrictedScripts } from 'hooks/useConsentRestrictedScripts';
import { useEffect } from 'react';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useLocation } from 'react-router';
import { useMountEffect, usePrevious } from '@tock/hooks';
import { useSelector } from 'react-redux';
import platform from 'platform';

const Amplitude: React.FunctionComponent = () => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const tockHistory = useSelector((state: StoreType) => state.history.locationHistory);
  const location = useLocation();
  const { consentLevelFunctional } = useConsentRestrictedScripts();
  const { amplitudeKey } = useSelector(getTockAmplitudePropertiesSelector);
  const enableSessionReplay = useFeatureFlagForConsumer('AMPLITUDE_SESSION_REPLAY');

  const searchParams = parse(location.search);
  const tockSourceParam: string | undefined = searchParams?.['tock_source'];
  const tockMediumParam: string | undefined = searchParams?.['tock_medium'];

  const prevConsentLevelFunctional = usePrevious(consentLevelFunctional);
  const business = useSelector((state: StoreType) => state.app.config?.business);
  const { offerings, calendar } = useSelector((state: StoreType) => state.calendar);
  const patron = useSelector((state: StoreType) => state.app.patron);
  const experimentVariants: Dictionary<ExperimentVariant> = useSelector(
    (state: StoreType) => state.experiments.variants
  );

  const sendPageview = () => {
    // pathname and title will get appended to data object in middleware
    const varieties = uniq(
      offerings?.experience
        .map((experience) => {
          if (isPaymentCardHoldExperience(experience)) {
            return 'PAYMENT_CARD_HOLD';
          }
          return experience.type;
        })
        .filter(filterNilValues)
    );

    const hasExperienceImages =
      !!offerings?.experience &&
      offerings.experience.findIndex(
        (experience) => experience.image && experience.image.length > 0
      ) !== -1;
    const acceptingReservations = !!calendar;
    const numExperiencesWithMoreThanOneSeatingOption = offerings?.experience
      .map((experience) => experience.seatingArea?.length ?? 0)
      .filter((numSeatingOptions) => numSeatingOptions > 1).length;

    amplitudeSendEvent(ACTION_VIEW_PAGE, {
      pageType: getPageType(),
      ...businessToData(business),
      offeringType: varieties,
      experienceImages: hasExperienceImages,
      acceptingReservations,
      numExperiencesWithSeatingOptions: numExperiencesWithMoreThanOneSeatingOption,
    });
  };

  const getPageType = (): PageType => {
    const isOfferingDetailPage = location.pathname.includes('/event/');

    if (isOfferingDetailPage) {
      return 'Offering detail';
    }

    const isCheckout = location.pathname.includes('/checkout');

    if (isCheckout) {
      return 'Checkout';
    }

    if (business) {
      return 'Business';
    }

    return 'Tock';
  };

  useEffect(() => {
    if (consentLevelFunctional) {
      setupAmplitudeTracking(amplitudeKey, enableSessionReplay);
    }
    if (prevConsentLevelFunctional && !consentLevelFunctional) {
      revokeAmplitudeTracking();
    }
  }, [amplitudeKey, consentLevelFunctional, prevConsentLevelFunctional, enableSessionReplay]);

  useEffect(() => {
    if (patron) {
      amplitudeSetUserId(patron.uuid);
      amplitudeSetUserPropertyOnce({ property: 'PatronId', value: patron.id });
    }
  }, [patron]);

  useEffect(() => {
    const experimentAssignments = Object.entries(experimentVariants).map((entry) => {
      const key = entry[0];
      const variant = entry[1].variant || 0;
      return `${key}:${variant}`;
    });
    amplitudeSetUserProperty({ property: 'Experiments', value: experimentAssignments });
  }, [experimentVariants]);

  useMountEffect(() => {
    platform.name && amplitudeSetUserProperty({ property: 'Browser type', value: platform.name });
    platform.version &&
      amplitudeSetUserProperty({
        property: 'Browser version',
        value: platform.version,
      });
    tockSourceParam &&
      amplitudeSetUserProperty({ property: 'Tock source', value: tockSourceParam });
    tockMediumParam &&
      amplitudeSetUserProperty({ property: 'Tock medium', value: tockMediumParam });

    const isReload =
      window.performance
        .getEntriesByType('navigation')
        .map((performanceEntry) => (performanceEntry as PerformanceNavigationTiming).type)
        .findIndex((type) => type === 'reload') !== -1;
    const hasHistory = (tockHistory || []).length > 1;

    if (!isReload && !hasHistory) {
      amplitudeSetUserProperty({ property: 'Landing page', value: location.pathname });
      amplitudeSetUserProperty({ property: 'Landing page type', value: getPageType() });
    }
    return;
  });

  useEffect(() => {
    sendPageview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

export default Amplitude;
